import { useCallback, useState } from "react";
// Controls
import { ActionButton, Label, useTheme } from "@fluentui/react";
import { TextField, TextFieldType } from "../../textField/TextField";
import { CommentCard } from "../commentCard/CommentCard";
import { Post } from "@ms/uno-models/lib/local/client/post/Post";
import { FormerMember } from "@ms/uno-models/lib/local/client/user/FormerUser";
import { UserBuilder } from "@ms/uno-models/lib/local/client/user/UserBuilder";
import { ContentType } from "@ms/uno-models/lib/local/service/graph/general/ContentType";
// Styles
import { getClassNames, getStyles } from "./CommentThreadView.styles";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import { escapeCommentText, getCommentReplyEmailContent, getFirstCommentEmailContent, getPlainTextFromHTMLContent } from "@ms/uno-models/lib/local/utilities/CommentUtilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import trim from "lodash/trim";
import moment from "moment";
import * as React from "react";
/**
 * Component for rendering a comment thread
 */ export function CommentThreadView(props) {
    const theme = useTheme();
    const [isCollapsed, setIsCollapsed] = useState(props.commentThread.length > props.collapseThreshold);
    const [typedComment, setTypedComment] = useState("");
    /**
     * Get the portion of the comment thread that can be rendered
     */ const getRenderableCommentThread = useCallback(()=>{
        // Reverse the thread array to get most recent comment first
        const reversedThread = props.commentThread.slice().reverse();
        return isCollapsed ? reversedThread.slice(0, props.collapseThreshold) : reversedThread;
    }, [
        props.commentThread,
        props.collapseThreshold,
        isCollapsed
    ]);
    const classNames = getClassNames(getStyles(theme, props.styles));
    const commentThread = getRenderableCommentThread();
    const newCommentSection = renderNewCommentSection();
    /**
     * Render the send new comment section
     */ function renderNewCommentSection() {
        const commentError = /*#__PURE__*/ React.createElement(Label, {
            className: "commentLoadError"
        }, Strings.TaskComments_CommentsLoadError);
        const loadCommentError = props.showLoadingError ? commentError : null;
        const postDisabled = trim(typedComment).length === 0 || props.disabled;
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.newCommentSection
        }, /*#__PURE__*/ React.createElement(TextField, {
            autoAdjustHeight: true,
            className: classNames.commentBox,
            multiline: true,
            value: typedComment,
            placeholder: Strings.TaskComments_CommentBoxPlaceholder,
            resizable: false,
            onChange: onCommentChanged,
            styleType: TextFieldType.Dialog,
            ariaLabel: Strings.TaskComments_CommentBoxAriaLabel,
            disabled: props.disabled,
            title: props.disabled ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined
        }), loadCommentError, /*#__PURE__*/ React.createElement("button", {
            "aria-label": Strings.TaskComments_SendCommentButtonLabel,
            className: classNames.sendCommentButton,
            disabled: postDisabled,
            title: props.disabled ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined,
            onClick: onSendCommentClick
        }, Strings.TaskComments_SendCommentButtonLabel));
    }
    /**
     * Render the expand comments section
     */ function renderExpandCommentsSection() {
        if (isCollapsed) {
            return /*#__PURE__*/ React.createElement(ActionButton, {
                className: classNames.expandCommentsSection,
                onClick: toggleThreadView,
                ariaLabel: StringHelper.format(Strings.TaskComments_ExpandCommentsSectionTextAriaLabel, props.commentThread.length.toString())
            }, StringHelper.format(Strings.TaskComments_ExpandCommentsSectionText, props.commentThread.length.toString()));
        } else {
            return null;
        }
    }
    /**
     * Helper method to get the commenter or provide a default one if one cannot be found in our user records
     * @param email Email of the commenter
     */ function getCommenter(email) {
        return isEmpty(email) ? null : props.commentViewCallbacks.getUserByEmail(email);
    }
    /**
     * Toggle the thread view expand/collapse state
     */ function toggleThreadView() {
        setIsCollapsed((prevState)=>!prevState);
    }
    /**
     * Callback for updating the state with the freshly typed comment from the comment box
     * @param comment The latest comment value
     */ function onCommentChanged(event, comment) {
        setTypedComment(comment);
    }
    /**
     * Handler for the post comment button clicks
     */ function onSendCommentClick() {
        const formattedComment = escapeCommentText(typedComment);
        const emailContent = props.task.conversationThreadId == null ? getFirstCommentEmailContent(props.taskLink, props.planLink, props.planTitle) : getCommentReplyEmailContent(props.taskLink, props.task.title, props.planLink, props.planTitle);
        const message = formattedComment + emailContent;
        const postBody = Post.generatePostBody(message);
        const newPost = new Post({
            body: postBody,
            createdDateTime: new Date(),
            fromField: {
                emailAddress: {
                    address: props.currentUser.mail ? props.currentUser.mail : props.currentUser.userPrincipalName,
                    name: props.currentUser.displayName
                }
            },
            changeKey: "",
            id: LocalIdGenerator.generate(),
            lastModifiedDateTime: new Date(),
            receivedDateTime: new Date(),
            sender: {
                emailAddress: {
                    address: props.currentUser.mail ? props.currentUser.mail : props.currentUser.userPrincipalName,
                    name: props.currentUser.displayName
                }
            }
        });
        setTypedComment("");
        props.commentViewCallbacks.postComment(newPost);
    }
    /**
     * Render the comment thread content
     */ function renderCommentThreadContent() {
        return /*#__PURE__*/ React.createElement("ul", null, map(commentThread, (commentData)=>{
            let commenter = getCommenter(commentData.fromField.emailAddress.address);
            const commenterPhoto = commenter != null ? props.commentViewCallbacks.getUserPhoto(commenter.id) : FormerMember.Photo;
            if (commenter == null && commentData.fromField.emailAddress.name && commentData.fromField.emailAddress.address) {
                // If haven't fetched commenter yet, use the fromField to create a temporary commenter
                commenter = new UserBuilder().withPropertyBag({
                    displayName: commentData.fromField.emailAddress.name,
                    id: commentData.fromField.emailAddress.address
                }).build();
            }
            let commentText = commentData.body?.content || "";
            if (commentData.body?.contentType === ContentType.HTML) {
                commentText = getPlainTextFromHTMLContent(commentText);
            }
            // Construct the aria label for the comment card
            const createdByAriaLabelPart = commentData.sender != null ? StringHelper.format(Strings.TaskComments_CommentCardCreatedByAriaLabelPart, commentData.sender.emailAddress.name) : "";
            const createdDateAriaLabelPart = commentData.createdDateTime != null ? StringHelper.format(Strings.TaskComments_CommentCardCreatedDateAriaLabelPart, moment(commentData.createdDateTime).format("LLL")) : "";
            const ariaLabel = StringHelper.format(Strings.TaskComments_CommentCardAriaLabel, createdByAriaLabelPart, createdDateAriaLabelPart, commentText);
            return /*#__PURE__*/ React.createElement("li", {
                key: commentData.id,
                "aria-label": ariaLabel
            }, /*#__PURE__*/ React.createElement(CommentCard, {
                comment: commentData.body?.content || null,
                commentId: commentData.id,
                commenter: commenter,
                commenterPhoto: commenterPhoto ?? null,
                commentTime: moment(commentData.createdDateTime),
                commentType: commentData.body?.contentType || null,
                onRenderPersona: props.commentViewCallbacks.onRenderPersona,
                onReloadCommentClicked: props.commentViewCallbacks.getConversationThreadPost,
                commentRefreshEnabled: props.commentRefreshEnabled
            }));
        }));
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.root
    }, /*#__PURE__*/ React.createElement(Label, {
        className: classNames.threadViewLabel
    }, Strings.TaskComments_ThreadViewLabel), newCommentSection, renderCommentThreadContent(), renderExpandCommentsSection());
}
